import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ProductType } from '../shared/models/auth-state.model';
import { User } from '../shared/models/user.model';
import { AuthStateService } from '../shared/services/auth-state.service';
import { BrandService } from '../shared/services/brand.service';
import { LoginService } from '../shared/services/login.service';
import { SsoAuthService } from './services/sso-auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public loginFormGroup!: FormGroup;
  public loggingIn = false;
  private redirectUrl!: string;
  passwordFieldType: string = 'password';

  constructor(
    public router: Router,
    private fb: FormBuilder,
    private login: LoginService,
    private ssoAuth: SsoAuthService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private brandService: BrandService,
    private authState: AuthStateService,
  ) {
    this.loginFormGroup = this.fb.group({
      username: new FormControl(null, Validators.required),
      password: new FormControl(null, Validators.required),
      rememberMe: [false],
    });
  }

  ngOnInit() {
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || '/';

    if (this.login.isTokenValid()) {
      this.router.navigate(['/home'], { replaceUrl: true });
    }
  }

  public logIn(): void {
    if (this.loginFormGroup.invalid) {
      return;
    }

    this.loggingIn = true;
    const loginObservable = this.login.login(
      this.loginFormGroup.controls['username'].value,
      this.loginFormGroup.controls['password'].value,
    );
    loginObservable.subscribe({
      next: (res) => {
        const ctx = this.brandService.context;
        const resolvedDefaultCategoryId = res.defaultCategoryId ?? ctx?.defaultCategoryId ?? null;
        const authState = {
          token: res.token,
          username: res.username,
          userId: res.userId,
          roles: res.roles ?? [],
          productType: (res.productType as ProductType) ?? 'IR',
          brandId: res.brandId ?? ctx?.brandId ?? null,
          categories: res.categories ?? ctx?.categories ?? [],
          defaultCategoryId: resolvedDefaultCategoryId,
          activeCategoryId: resolvedDefaultCategoryId,
        };
        if (this.loginFormGroup.controls['rememberMe'].value) {
          this.authState.save(authState);
        } else {
          this.authState.saveSession(authState);
        }
        this.login.setUser({
          id: res.userId,
          username: res.username,
          fullName: res.fullName || [res.firstname, res.lastname].filter(Boolean).join(' ') || res.username,
          email: res.email ?? '',
          roles: res.roles ?? [],
          productType: (res.productType as ProductType) ?? 'IR',
        } as User);
        this.router.navigateByUrl(this.brandService.current.homeRoute);
      },
      error: () => {
        this.loggingIn = false;
        const errorMsg = this.translate.instant('ERROR_MESSAGES.INVALID_CREDENTIALS');
        this.showErrorNotification(errorMsg);
      },
    });
  }

  public loginWithSSO(): void {
    this.ssoAuth.loginWithMicrosoft();
  }

  public get usernameField() {
    return this.loginFormGroup.get('username');
  }

  public get passwordField() {
    return this.loginFormGroup.get('password');
  }

  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  private showErrorNotification(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
    });
  }

  get logoSrc(): string {
    return this.brandService.current.logoSrc;
  }

  get loginBgStyle(): string {
    const src = this.brandService.current.loginBgSrc;
    // Guard against CSS injection: only allow simple relative asset paths.
    const safeSrc = /^assets\/[\w\-\/]+\.(?:jpg|jpeg|png|svg|webp)$/.test(src) ? src : 'assets/img/login_background.jpg';
    return `url('${safeSrc}')`;
  }

  get requestAccessMailto(): string {
    const brand = this.brandService.current;

    const subject = encodeURIComponent(this.translate.instant('LOGIN.REQUEST_ACCESS'));

    // Try to use username if it looks like an email
    const rawUser = (this.loginFormGroup.get('username')?.value || '').trim();
    const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawUser) ? rawUser : '';

    const describe = this.translate.instant('LOGIN.REQUEST_ACCESS_BODY');
    const pleaseEnter = this.translate.instant('LOGIN.PLEASE_ENTER_EMAIL');

    const requesterLine = `Requester email: ${emailLike || `(${pleaseEnter})`}`;
    const langLine = `Language: ${this.translate.currentLang}`;
    const urlLine = `URL: ${brand.publicUrl ?? this.router.url}`;
    const brandLine = `Brand: ${brand.id}`;

    const body = encodeURIComponent(`${describe}\n\n${requesterLine}\n${langLine}\n${urlLine}\n${brandLine}\n`);

    return `mailto:${brand.supportEmail}?subject=${subject}&body=${body}`;
  }
}
